import { html, TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { WEB_COMPONENT_GENERAL_DONUT_CHART } from '../../../webcomponents.constants';
import bb from 'billboard.js/dist/billboard.pkgd';
import { styles } from '../view/styles';
import { GzGenericWidgetContent } from '../../../genericDashboard/gzGenericWidget';
import { GeneralDonutChartInput, GzDonutChartEvents, GzGeneralDonutChartPayload, PieChart } from '../types';
import { WidgetStateFrontEnd } from '../../../genericDashboard/gzGenericWidget/types';

@customElement(WEB_COMPONENT_GENERAL_DONUT_CHART)
export class GzGeneralDonutChartWebComponent extends GzGenericWidgetContent {
  static get styles() {
    return styles;
  }

  @property({ type: Object, attribute: true, reflect: true })
  payload: GzGeneralDonutChartPayload;

  @property({ type: String, attribute: true, reflect: true })
  heightAndWidth: string;

  @query('#chart-content')
  private chartContent: HTMLElement;

  @query('#legend-content')
  private legendContent: HTMLElement;

  @query('#generalDonutChartContainer')
  private generalDonutChartContainer: HTMLElement;

  @query('#chart-legend-container')
  private sectionContent: HTMLElement;

  private verticalPadding: number;

  private mapLabelToId: any = {};

  private emptyStateColor;
  public noDataAvailable: () => Node;
  public errorLoadingData: () => Node;

  public firstUpdated(): void {
    super.firstUpdated();
    const { verticalPadding } = this.getPaddingConstants();
    this.verticalPadding = verticalPadding;
    this.emptyStateColor = getComputedStyle(this).getPropertyValue('--grey-layout-203-dark-theme-disabled-text');
  }

  private buildChart(): void {
    if (!this.payload) {
      return;
    }

    if (this.chartContent) {
      this.chartContent.innerHTML = '';
    } else {
      return;
    }

    const activeStateData = this.payload as GeneralDonutChartInput;
    const chartTitle = activeStateData.title;
    let columns: (string | number)[][] = [];
    let chartValue = 0;
    const colorsFromPayload = {};

    if (this.state !== WidgetStateFrontEnd.IS_ACTIVE) {
      columns = [['empty', 100]];
    } else {
      for (const slice of activeStateData.data) {
        this.mapLabelToId[slice.label] = slice.id;
        columns.push([slice.label, slice.value]);
        chartValue += slice.value;
        if (slice.color) {
          colorsFromPayload[slice.label] = slice.color;
        }
      }
    }

    const displayChartValue = this.state === WidgetStateFrontEnd.IS_ACTIVE ? chartValue : `_ _`;

    const colors =
      this.state !== WidgetStateFrontEnd.IS_ACTIVE
        ? {
            ['empty']: this.emptyStateColor,
          }
        : colorsFromPayload;

    const chart = this.generateChart(columns, displayChartValue, chartTitle, colors);

    if (this.heightAndWidth) {
      chart.resize({
        width: this.heightAndWidth,
        height: Number(this.heightAndWidth) / 2,
      });
    }

    if (this.state === WidgetStateFrontEnd.IS_EMPTY) {
      this.legendContent.style.display = 'none';
      this.chartContent.appendChild(this.noDataAvailable());
    }

    if (this.state === WidgetStateFrontEnd.IS_ERROR) {
      this.legendContent.style.display = 'none';
      this.chartContent.appendChild(this.errorLoadingData());
    }
  }

  private splitTitleIntoLines(title: string): string {
    const words = title.split(' ');
    return words.join('\n');
  }

  private handleClickFromLegend(id: string): void {
    this.dispatchEvent(
      new CustomEvent(GzDonutChartEvents.OPEN_SIDE_PANEL_RISKS, {
        detail: {
          idChart: this.mapLabelToId[id],
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleClickFromPieChart(element: PieChart): void {
    const id = this.mapLabelToId[element.id];
    this.dispatchEvent(
      new CustomEvent(GzDonutChartEvents.OPEN_SIDE_PANEL_RISKS, {
        detail: {
          idChart: id,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private generateChart(
    columns: (string | number)[][],
    displayChartValue: number | string,
    chartTitle: string,
    colors: object
  ) {
    const topPadding = 0;
    const bottomPadding = 0;
    const leftPadding = 0;
    const rightPadding = 0;
    return bb.generate({
      data: {
        columns: columns,
        onclick: this.handleClickFromPieChart.bind(this),
        type: 'donut',
        colors: colors,
        //null: It keeps the data load order
        order: null,
      },
      padding: {
        top: topPadding,
        right: rightPadding,
        bottom: bottomPadding,
        left: leftPadding,
      },
      donut: {
        title: `${displayChartValue}\n${this.splitTitleIntoLines(chartTitle)}`,
        label: {
          show: false,
        },
        width: 14,
        padAngle: 0.1,
      },
      tooltip: {
        show: false,
      },
      legend: {
        show: true,
        position: 'bottom',
        item :{
          onclick: this.handleClickFromLegend.bind(this),
        },
        contents: {
          bindto: this.legendContent,
          template: function (title, color, data) {
            return `<li>
                      <span style="background-color:${color};"></span>
                      <div class="legend-title">${title}</div>
                      <div class="legend-value">${data[0].value}</div>
                    </li>`;
          },
        },
      },
      interaction: {
        enabled: true,
      },
      bindto: this.chartContent,
    });
  }

  async updated(): Promise<void> {
    await this.updateComplete;
    this.buildChart();
  }

  protected renderLoader(): TemplateResult {
    return html`
      <div class="loading-container">
        <gz-loader class="flex-3"></gz-loader>
        <gz-loader class="flex-5"></gz-loader>
      </div>
    `;
  }

  public renderContent(): TemplateResult {
    return html`
      <div id="generalDonutChartContainer">
        <div id="chart-content"></div>
        <div id="legend-content"></div>
      </div>
    `;
  }
}
